// React
import { Fragment, useContext, useEffect, useState } from "react"

// Components
import { BaseTable, Button, Card, CommandButton, ErrorText, HeaderMenu, Input } from "components"
import { Acc, ActionButton, Date, Deliver, Freight, Memo, PaymentApprovedSection, PromisedDate, Supplier, SupplierId, TabSection, TaxInclusive, TradingTerm } from "../../components"
import { Code } from "./Code"
import { Detail } from "./Detail"
import { Journal, Payment, Purchases } from "./displays"
import { JournalModals } from "./JournalModal"

// Contexts
import { DataJournalContext } from "contexts"
import { DisabledContext } from "../../contexts"
import { DataBillContext, DataContext, ProbilContext, ProductContext, ProductUnitProvider } from "../contexts"

// Form
import { useFieldArray, useFormContext, useWatch } from "react-hook-form"
import { ErrorMessage } from "@hookform/error-message"

// Icons
import { TbPlus } from "react-icons/tb"

// Types
import type { DetailFormType, FormType } from "../utils"

// Utils
import { useProject, useTaxCode } from "utils"
import { duplicateChecker, tab_list } from "../utils"
import { useProduct } from "../../PurchaseOrder/components/DetailBody/utils"

export function InputSection(params: {
  onSubmit?: () => void
}): JSX.Element {
  // Hooks
  const bill = useContext(DataBillContext)
  const disabled = useContext(DisabledContext)
  const project = useProject()
  const tax_code = useTaxCode()
  const dataBill = useContext(DataContext)
  const { data, isLoading, refetch } = useProduct()

  // Vars
  const isPending: boolean = dataBill.datapo.po_delivery_status === 1
  
  // Form
  const { control, formState, getValues } = useFormContext<FormType>()
  const vendor_id = useWatch({
    control,
    name: "vendor_id"
  })
  const { fields, append, remove } = useFieldArray({
    control,
    name: "probildetail"
  })

  useEffect(() => {
    if (vendor_id) {
      refetch(vendor_id)
    }

    // eslint-disable-next-line
  }, [vendor_id])

  return (
    <section className="container my-5 flex flex-col gap-2">
      <HeaderMenu title="DATA ENTRY | DELIVERY BILL">
        <Code value={getValues("trx_code")} />
      </HeaderMenu>

      <section className="grid lg:grid-cols-3 gap-x-6 gap-y-3 items-end">
        <Supplier
          disabled
          name="vendor_id"
          trading_term_name="payment_term"
          vendor_name="_vendor_name"
        />

        <TradingTerm name="payment_term" />
        <TaxInclusive name="tax_inclusive" />
      </section>

      <Card>
        <Card.Body className="grid lg:grid-cols-2 gap-x-6 gap-y-3">
          <section className="flex flex-col gap-3">
            <Deliver
              deliver_name="location_id"
              detail_name="address"
            />

            <Memo name="memo" />
            <LinkToPurchase />
          </section>

          <section className="flex flex-col gap-3">
            <Date
              name="transaction_date"
              disabled={!isPending}
            />

            <SupplierId
              name="referensi"
              disabled={!isPending}
            />

            <PromisedDate
              disabled
              name="promise_date"
            />

            <Delivery />

            <section className="flex lg:justify-end">
              <Button className="w-fit">
                <TbPlus /> ADD REMINDER
              </Button>
            </section>
          </section>
        </Card.Body>
      </Card>

      <DataJournalContext.Provider value={{ project, tax_code }}>
        <ProductContext.Provider value={{ data, isLoading }}>
          <ProductUnitProvider>
            <BaseTable>
              <thead>
                <tr>
                  <th>BACKORDER</th>
                  <th>DELIVERED</th>
                  <th>ITEM CODE</th>
                  <th>DESCRIPTION</th>
                  <th>UNIT</th>
                  <th>PRICE</th>
                  <th>DISC (%)</th>
                  <th>TOTAL</th>
                  <th>JOB</th>
                  <th>TAX</th>
                  <th>ACTION</th>
                </tr>
              </thead>

              <tbody>
                {fields.map((_, key) => (
                  <Detail
                    key={key}
                    index={key}
                    remove={remove}
                  />
                ))}
              </tbody>
            </BaseTable>
          </ProductUnitProvider>

          <ErrorMessage
            name="probildetail"
            errors={formState.errors}
            render={({ message }) => <ErrorText text={message} />}
          />

          <section className="grid lg:grid-cols-2 gap-x-6 gap-y-3">
            <section className="flex flex-col gap-3">
              <PaymentStatus />
              <Freight name="freight" />
            </section>

            <Acc
              name={{
                detail: "probildetail",
                freight: "freight"
              }}
            />
          </section>

          <ProbilContext.Provider value={{
            append: (value: DetailFormType) => {
              // Vars
              const data = duplicateChecker(value, getValues("probildetail"), bill.approval_status === "Approved")

              if (data) {
                append(value)
              }
            }
          }}>
            <Tab />
          </ProbilContext.Provider>
        </ProductContext.Provider>
      </DataJournalContext.Provider>

      {!disabled && (
        <ActionButton>
          <section className="flex flex-wrap gap-3">
            <CommandButton actiontype="help" />
            <CommandButton actiontype="print" />
            <CommandButton actiontype="email" />
            <CommandButton actiontype="export" />

            <DataJournalContext.Provider value={{ project, tax_code }}>
              <ProductContext.Provider value={{ data, isLoading }}>
                <JournalModals />
              </ProductContext.Provider>
            </DataJournalContext.Provider>

            <CommandButton actiontype="attachment" />
          </section>

          <SubmitButton
            onSubmit={params.onSubmit}
          />
        </ActionButton>
      )}
    </section>
  )
}

function Delivery(): JSX.Element {
  // Form
  const { getValues } = useFormContext<FormType>()

  return (
    <Input
      disabled
      label="DELIVERY"
      value={`#${getValues("delivery_no")}`}
    />
  )
}

function LinkToPurchase() {
  // Hooks
  const data = useContext(DataContext)

  return (
    <Input
      disabled
      label="LINK TO PURCHASE ID"
      value={data.datapo.trx_code}
    />
  )
}

function PaymentStatus(): JSX.Element {
  // Hooks
  const dataBill = useContext(DataBillContext)

  return (
    <Fragment>
      <Input
        disabled
        label="BILL STATUS"
        value={dataBill.bill_status}
      />

      <PaymentApprovedSection
        approved_by={dataBill.approved_by}
        payment_status={dataBill.approval_status}
      />
    </Fragment>
  )
}

function SubmitButton(params: {
  onSubmit?: () => void
}): JSX.Element {
  // Hooks
  const dataBill = useContext(DataBillContext)

  if (dataBill.approval_status !== "Approved") {
    return (
      <section className="flex flex-wrap gap-3">
        <CommandButton actiontype="delete" />

        <CommandButton
          actiontype="save"
          onClick={params.onSubmit}
          // permission="PT051"
          // loading={methods.formState.isSubmitting ? "true" : undefined}
        />
      </section>
    )
  }

  return <div />
}

function Tab(): JSX.Element {
  // Vars
  const content = [Purchases, Payment, Journal]

  // Hooks
  const [currentTab, setCurrentTab] = useState<number>(0)

  return (
    <section>
      <TabSection
        activeIndex={currentTab}
        list={tab_list}
        onChangeTab={setCurrentTab}
      />

      {content.map((Element, key) => {
        if (currentTab === key) {
          return <Element key={key} />
        }

        return null
      })}
    </section>
  )
}